/**
 * Latori: gift with purchase ("Geschenkartikel").
 *
 * Shopify's native discounts can price an item at 0 but they cannot put it
 * into the cart, so the theme does the adding. This file holds two custom
 * elements:
 *
 *   <gift-with-purchase-latori>   reconciles the cart against the gift rules
 *                                 configured on the cart drawer section
 *   <cart-discount-code-latori>   discount code entry, so the "discount code"
 *                                 condition is reachable before checkout
 *
 * Gift lines carry the hidden line item property `_free_gift` (the rule's
 * block id) plus an optional `_free_gift_note`. A Shopify Function matches
 * that property and discounts the line to 0 - this file never touches prices.
 *
 * Everything lives in one IIFE so the two elements can share helpers while
 * each keeps the theme's usual customElements.get() registration guard.
 */
(() => {
  /**
   * Applied discount codes in their original casing. The Ajax cart exposes
   * these as `discount_codes`; the `discount_applications` shape is read as a
   * fallback so a change in the API response can't silently disable the code
   * condition. Liquid has no equivalent - the cart object carries no
   * discount_codes property - which is why this is read client-side.
   *
   * `keepCodes` are counted as applied even while Shopify reports them as
   * `applicable: false`. Once the minimum is met, the gift app's Function
   * keeps a gift code applicable on its own (a 0.01 hold discount until the
   * gift line exists, see the gift app's `holdCode`). Below the minimum - or
   * on a discount without `holdCode` - it stays inapplicable until the gift is
   * in the cart, and the gift only goes in once the code is. Dropping those
   * would deadlock.
   */
  function readAppliedCodes(cart, keepCodes = []) {
    if (!cart) return [];
    if (Array.isArray(cart.discount_codes)) {
      return cart.discount_codes
        .filter(
          (entry) =>
            entry &&
            entry.code &&
            (entry.applicable !== false || includesCode(keepCodes, entry.code)),
        )
        .map((entry) => String(entry.code));
    }
    const applications = cart.discount_applications || [];
    return applications
      .filter((entry) => entry && entry.type === 'discount_code' && entry.title)
      .map((entry) => String(entry.title));
  }

  function includesCode(codes, code) {
    if (!code) return false;
    const needle = String(code).toLowerCase();
    return codes.some((entry) => String(entry).toLowerCase() === needle);
  }

  /**
   * Codes that trigger a gift rule, read from the reconciler's server-rendered
   * config (rendered once per page, outside the cart's merge regions).
   */
  function readGiftRules() {
    const script = document.querySelector(
      'gift-with-purchase-latori script[type="application/json"]',
    );
    if (!script) return [];
    try {
      const config = JSON.parse(script.textContent);
      return Array.isArray(config.rules) ? config.rules : [];
    } catch (e) {
      return [];
    }
  }

  function readGiftCodes() {
    return readGiftRules()
      .filter((rule) => String(rule.condition).indexOf('code') !== -1 && rule.code)
      .map((rule) => String(rule.code));
  }

  /**
   * The cart value a gift threshold is measured on: every non-gift line at
   * its price before line discounts - the same measure the reconciler and
   * the gift app's Function use.
   */
  function giftSubtotal(cart) {
    return ((cart && cart.items) || [])
      .filter((item) => !(item.properties && item.properties._free_gift))
      .reduce((sum, item) => sum + item.original_line_price, 0);
  }

  /**
   * The first applied gift code whose rule also needs a minimum order value
   * the cart doesn't reach yet. Such a code is kept in the cart (see
   * readAppliedCodes), so without this the customer would read it as
   * redeemed although neither the discount nor the gift applies yet.
   */
  function findPendingGiftCode(cart, codes) {
    const subtotal = giftSubtotal(cart);
    const rule = readGiftRules().find(
      (entry) =>
        entry.code &&
        String(entry.condition).indexOf('amount') !== -1 &&
        entry.threshold > 0 &&
        subtotal < entry.threshold &&
        includesCode(codes, entry.code),
    );
    return rule
      ? {
          code: codes.find(
            (code) => String(code).toLowerCase() === String(rule.code).toLowerCase(),
          ),
          amount: rule.thresholdFormatted || '',
        }
      : null;
  }

  /**
   * Runs the gift reconciler right away instead of after its debounce, so a
   * code change and the gift it adds or removes reach the drawer in one
   * refresh - no flash of the Function's 0.01 hold discount, no gift briefly
   * shown at full price. Resolves to whether the reconciler changed the cart
   * (it then dispatched on:cart:change itself).
   */
  function reconcileGifts() {
    const element = document.querySelector('gift-with-purchase-latori');
    return element && typeof element.reconcileNow === 'function'
      ? element.reconcileNow()
      : Promise.resolve(false);
  }

  async function fetchCart() {
    try {
      const response = await fetch(`${theme.routes.cart}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (e) {
      return null;
    }
  }

  /** POSTs a cart mutation. Resolves to the cart on success, null on error. */
  async function postCart(url, body) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (e) {
      return null;
    }
  }

  /** Debounce for cart events, ms. Long enough to coalesce a burst of
   *  quantity clicks, short enough that the gift lands while the drawer's
   *  open animation is still running. */
  const DEBOUNCE = 250;

  /** Reconcile passes per run. A pass that changes nothing ends the run, so
   *  this only caps pathological configurations. */
  const MAX_PASSES = 3;

  /** Cart mutations per page load - a backstop against an add/remove loop
   *  no one anticipated. */
  const MAX_MUTATIONS = 12;

  const FAILED_STORAGE_KEY = 'gift-with-purchase-latori:failed';

  if (!customElements.get('gift-with-purchase-latori')) {
    class GiftWithPurchaseLatori extends HTMLElement {
      connectedCallback() {
        // Never mutate a merchant's cart while they are customising the theme.
        if (window.Shopify && window.Shopify.designMode) return;

        this.rules = this.readRules();
        if (!this.rules.length) return;

        this.running = false;
        this.pending = false;
        this.mutationCount = 0;
        this.failedRuleIds = GiftWithPurchaseLatori.readFailedRuleIds();

        this.boundSchedule = this.schedule.bind(this);
        // A plain add-to-cart dispatches on:cart:add only (main.js's
        // ProductForm.handleSubmit updates the sections itself and skips
        // on:cart:change), quantity changes dispatch on:cart:change - listen
        // for both.
        document.addEventListener('on:cart:change', this.boundSchedule);
        document.addEventListener('on:cart:add', this.boundSchedule);

        this.schedule();
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        if (this.boundSchedule) {
          document.removeEventListener('on:cart:change', this.boundSchedule);
          document.removeEventListener('on:cart:add', this.boundSchedule);
        }
      }

      /** Parses the server-rendered rule config. */
      readRules() {
        const script = this.querySelector('script[type="application/json"]');
        if (!script) return [];
        try {
          const config = JSON.parse(script.textContent);
          return Array.isArray(config.rules) ? config.rules : [];
        } catch (e) {
          return [];
        }
      }

      /**
       * Rules whose gift could not be added (sold out, unpublished, quantity
       * rule) are remembered for the session, so a broken configuration
       * doesn't fire a request on every single cart change.
       */
      static readFailedRuleIds() {
        try {
          const stored = window.sessionStorage.getItem(FAILED_STORAGE_KEY);
          return new Set(stored ? JSON.parse(stored) : []);
        } catch (e) {
          return new Set();
        }
      }

      markRuleFailed(ruleId) {
        this.failedRuleIds.add(ruleId);
        try {
          window.sessionStorage.setItem(
            FAILED_STORAGE_KEY,
            JSON.stringify(Array.from(this.failedRuleIds)),
          );
        } catch (e) {
          // Private mode / storage full - the in-memory set still holds.
        }
      }

      schedule() {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.reconcile(), DEBOUNCE);
      }

      /** Skips the debounce - see reconcileGifts(). */
      reconcileNow() {
        if (!this.rules || !this.rules.length) return Promise.resolve(false);
        clearTimeout(this.timer);
        return this.reconcile();
      }

      /**
       * Brings the cart in line with the rules: adds every qualifying gift
       * that is missing, removes the ones that no longer qualify, and resets
       * a gift whose quantity was changed elsewhere.
       *
       * Single-flight - a cart event arriving mid-run only sets `pending`, so
       * two runs never interleave their reads and writes. Each run loops
       * until a pass finds nothing left to do, which is also what guarantees
       * termination: the on:cart:change this run dispatches at the end wakes
       * the component once more, that pass finds no diff, mutates nothing and
       * dispatches nothing.
       *
       * Resolves to whether the cart was changed.
       */
      async reconcile() {
        if (this.running) {
          this.pending = true;
          return false;
        }
        this.running = true;
        let mutated = false;

        try {
          for (let pass = 0; pass < MAX_PASSES; pass += 1) {
            if (this.mutationCount >= MAX_MUTATIONS) break;

            // eslint-disable-next-line no-await-in-loop
            const cart = await fetchCart();
            if (!cart) break;

            const ops = this.diff(cart);
            if (!ops.updates && !ops.adds.length && ops.discount === null) break;

            if (ops.updates || ops.discount !== null) {
              this.mutationCount += 1;
              const body = {};
              if (ops.updates) body.updates = ops.updates;
              if (ops.discount !== null) body.discount = ops.discount;
              // eslint-disable-next-line no-await-in-loop
              const updated = await postCart(theme.routes.cartUpdate, body);
              mutated = mutated || Boolean(updated);
            }

            for (let i = 0; i < ops.adds.length; i += 1) {
              if (this.mutationCount >= MAX_MUTATIONS) break;
              this.mutationCount += 1;
              // One request per gift, so a single unavailable gift doesn't
              // stop the others from being added.
              // eslint-disable-next-line no-await-in-loop
              const added = await postCart(theme.routes.cartAdd, {
                items: [ops.adds[i].item],
              });
              if (added) {
                mutated = true;
              } else {
                this.markRuleFailed(ops.adds[i].ruleId);
              }
            }
          }
        } finally {
          this.running = false;
          if (mutated) {
            // CartForm (drawer + cart page) and the header both refresh off
            // this event; see main.js.
            document.dispatchEvent(
              new CustomEvent('on:cart:change', { bubbles: true }),
            );
          }
          if (this.pending) {
            this.pending = false;
            this.schedule();
          }
        }
        return mutated;
      }

      /**
       * Compares the cart against the rules.
       * @returns {{updates: ?Object, adds: Array}} `updates` is a line-key
       *   keyed quantity map for /cart/update.js, `adds` the gift line items.
       */
      diff(cart) {
        const items = (cart && cart.items) || [];
        const giftLines = [];
        let realLineCount = 0;
        let subtotal = 0;

        items.forEach((item) => {
          const ruleId =
            item.properties && item.properties._free_gift
              ? String(item.properties._free_gift)
              : '';
          if (ruleId) {
            giftLines.push({ item, ruleId });
          } else {
            realLineCount += 1;
            // The threshold is measured on what the customer actually chose,
            // before line discounts - like the gift app's Function and checkout
            // extension. After discounts, the Function's 0.01 hold on a gift
            // code would pull a cart of exactly the minimum below it, and the
            // gift would never be added. The gift's own line never counts.
            subtotal += item.original_line_price;
          }
        });

        // A gift code is kept while "not applicable" only until its gift is
        // in the cart. If Shopify still rejects it then, the reason is a real
        // one (discount ended, paused, usage limit, customer eligibility) -
        // the gift would be charged at full price, so code and gift go.
        const cartCodes = Array.isArray(cart && cart.discount_codes)
          ? cart.discount_codes.filter((entry) => entry && entry.code)
          : [];
        const rejectedCodes = this.rules
          .filter(
            (rule) =>
              rule.code &&
              giftLines.some(
                ({ item, ruleId }) =>
                  ruleId === rule.id && item.variant_id === rule.variantId,
              ) &&
              cartCodes.some(
                (entry) =>
                  entry.applicable === false &&
                  String(entry.code).toLowerCase() === String(rule.code).toLowerCase(),
              ),
          )
          .map((rule) => rule.code);

        const codes = readAppliedCodes(
          cart,
          this.rules.map((rule) => rule.code).filter(Boolean),
        ).filter((code) => !includesCode(rejectedCodes, code));
        const eligibleIds = new Set();
        // No gift without a real purchase - this is also what clears the
        // gifts when the last real item leaves the cart.
        if (realLineCount > 0) {
          this.rules.forEach((rule) => {
            // Every condition is an AND of the two checks it names; the
            // snippet guarantees the config each one needs is present, so a
            // check a condition doesn't name is simply skipped here.
            const needsCode = String(rule.condition).indexOf('code') !== -1;
            const needsAmount = String(rule.condition).indexOf('amount') !== -1;
            if (needsCode && !includesCode(codes, rule.code)) return;
            if (needsAmount && !(rule.threshold > 0 && subtotal >= rule.threshold)) return;
            eligibleIds.add(rule.id);
          });
        }

        const updates = {};
        const satisfied = new Set();
        giftLines.forEach(({ item, ruleId }) => {
          const rule = this.rules.find((r) => r.id === ruleId);
          // Drop the line when its rule is gone (block deleted, disabled or
          // its campaign ended), no longer qualifies, points at a different
          // variant now, or when an identical gift is already kept.
          if (
            !rule ||
            !eligibleIds.has(ruleId) ||
            satisfied.has(ruleId) ||
            item.variant_id !== rule.variantId
          ) {
            updates[item.key] = 0;
            return;
          }
          satisfied.add(ruleId);
          if (item.quantity !== 1) updates[item.key] = 1;
        });

        const adds = this.rules
          .filter(
            (rule) =>
              eligibleIds.has(rule.id) &&
              !satisfied.has(rule.id) &&
              !this.failedRuleIds.has(rule.id),
          )
          .map((rule) => {
            const properties = { _free_gift: rule.id };
            if (rule.note) properties._free_gift_note = rule.note;
            return {
              ruleId: rule.id,
              item: { id: rule.variantId, quantity: 1, properties },
            };
          });

        return {
          updates: Object.keys(updates).length ? updates : null,
          adds,
          // Full code list for /cart/update.js without the rejected gift
          // codes, or null when the codes stay as they are.
          discount: rejectedCodes.length
            ? cartCodes
                .map((entry) => String(entry.code))
                .filter((code) => !includesCode(rejectedCodes, code))
                .join(',')
            : null,
        };
      }
    }

    customElements.define('gift-with-purchase-latori', GiftWithPurchaseLatori);
  }

  if (!customElements.get('cart-discount-code-latori')) {
    class CartDiscountCodeLatori extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-discount-input]');
        this.applyButton = this.querySelector('[data-discount-apply]');
        this.errorEl = this.querySelector('[data-discount-error]');
        this.successEl = this.querySelector('[data-discount-success]');
        this.noteEl = this.querySelector('[data-discount-note]');
        this.listEl = this.querySelector('[data-discount-list]');
        if (!this.input || !this.applyButton) return;

        this.applyButton.addEventListener('click', () => this.apply());
        this.input.addEventListener('keydown', (evt) => {
          if (evt.key === 'Enter') {
            evt.preventDefault();
            this.apply();
          }
        });
        this.input.addEventListener('input', () => {
          this.setError('');
          this.setSuccess('');
        });

        theme.addDelegateEventListener(
          this,
          'click',
          '[data-discount-remove]',
          (evt, button) => {
            evt.preventDefault();
            this.removeCode(button.dataset.code);
          },
        );

        this.boundRender = this.renderCodes.bind(this);
        document.addEventListener('on:cart:change', this.boundRender);
        this.renderCodes();
      }

      disconnectedCallback() {
        if (this.boundRender) {
          document.removeEventListener('on:cart:change', this.boundRender);
        }
      }

      setBusy(busy) {
        this.applyButton.classList.toggle('is-loading', busy);
        this.applyButton.disabled = busy;
      }

      setError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || '';
        this.errorEl.hidden = !message;
      }

      /**
       * Green confirmation under the field once a code is in. The code is
       * user input, so it goes in via textContent, never innerHTML - the
       * translation carries a %%CODE%% placeholder for it.
       */
      setSuccess(code) {
        if (!this.successEl) return;
        const message = code
          ? (this.dataset.successMessage || '').replace('%%CODE%%', code)
          : '';
        // Put the text in its own span. Thus the check icon stays.
        const textEl =
          this.successEl.querySelector('[data-discount-success-text]') ||
          this.successEl;
        textEl.textContent = message;
        this.successEl.hidden = !message;
      }

      /** "Code saved, applies from X" for a gift code below its minimum. */
      setPendingNote(pending) {
        if (!this.noteEl) return;
        const message = pending
          ? (this.dataset.pendingMessage || '')
              .replace('%%CODE%%', pending.code)
              .replace('%%AMOUNT%%', pending.amount)
          : '';
        this.noteEl.textContent = message;
        this.noteEl.hidden = !message;
      }

      /**
       * Applies the entered code. /cart/update.js replaces the whole code
       * list, so the currently applied ones are read first and posted back
       * along with the new one. A code Shopify reports as not applicable is
       * stripped out again rather than left sitting in the cart.
       */
      async apply() {
        const code = this.input.value.trim();
        if (!code) return;

        this.setBusy(true);
        this.setError('');
        this.setSuccess('');
        try {
          const current = await this.readCodes();
          const next = current
            .filter((entry) => entry.toLowerCase() !== code.toLowerCase())
            .concat(code);
          const cart = await postCart(theme.routes.cartUpdate, {
            discount: next.join(','),
          });
          if (!cart) {
            this.setError(this.dataset.errorMessage);
            return;
          }

          const applied = readAppliedCodes(cart, readGiftCodes());
          if (!includesCode(applied, code)) {
            this.setError(this.dataset.invalidMessage);
            await postCart(theme.routes.cartUpdate, {
              discount: current.join(','),
            });
            this.renderCodeList(current);
            return;
          }

          this.input.value = '';
          this.renderCodeList(applied);
          const pending = findPendingGiftCode(cart, applied);
          this.setPendingNote(pending);
          if (!pending) this.setSuccess(code);
          // Adds a gift code's gift before the drawer refreshes.
          if (!(await reconcileGifts())) {
            document.dispatchEvent(
              new CustomEvent('on:cart:change', { bubbles: true }),
            );
          }
        } finally {
          this.setBusy(false);
        }
      }

      async removeCode(code) {
        if (!code) return;
        this.setBusy(true);
        this.setError('');
        this.setSuccess('');
        try {
          const current = await this.readCodes();
          const next = current.filter(
            (entry) => entry.toLowerCase() !== String(code).toLowerCase(),
          );
          // An empty list clears every code - that is what the empty string
          // does on /cart/update.js.
          const cart = await postCart(theme.routes.cartUpdate, {
            discount: next.join(','),
          });
          this.renderCodeList(
            cart ? readAppliedCodes(cart, readGiftCodes()) : next,
          );
          // Removes a gift code's gift before the drawer refreshes.
          if (!(await reconcileGifts())) {
            document.dispatchEvent(
              new CustomEvent('on:cart:change', { bubbles: true }),
            );
          }
        } finally {
          this.setBusy(false);
        }
      }

      async readCodes() {
        return readAppliedCodes(await fetchCart(), readGiftCodes());
      }

      /**
       * Re-renders the chips after every cart change and keeps the
       * minimum-order hint current: it disappears once the cart reaches the
       * gift's minimum (or the code is removed), and comes back if the
       * cart drops below it again.
       */
      async renderCodes() {
        const cart = await fetchCart();
        const codes = readAppliedCodes(cart, readGiftCodes());
        this.renderCodeList(codes);
        // The cart page's collapsed "Vorteilscode oder Gutschein" section only
        // starts open when Liquid sees an applied code, and a gift code kept
        // below its minimum isn't one - open it once from here, so its chip
        // and the "gilt ab X" note don't sit hidden after a reload. Only on
        // the first render: a section the customer closed stays closed.
        if (!this.revealedCodes && codes.length) {
          this.revealedCodes = true;
          const section = this.closest('details');
          if (section) section.open = true;
        }
        const pending = findPendingGiftCode(cart, codes);
        this.setPendingNote(pending);
        if (pending) this.setSuccess('');
      }

      renderCodeList(codes) {
        if (!this.listEl) return;
        this.listEl.textContent = '';
        this.listEl.hidden = !codes.length;
        codes.forEach((code) => {
          const li = document.createElement('li');
          li.className = 'cart-discount-code__chip';

          const label = document.createElement('span');
          // Codes come back from the API - always textContent, never
          // innerHTML.
          label.textContent = code;
          li.appendChild(label);

          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'cart-discount-code__chip-remove';
          button.dataset.discountRemove = '';
          button.dataset.code = code;
          button.setAttribute('aria-label', this.dataset.removeLabel || '');
          button.textContent = '×';
          li.appendChild(button);

          this.listEl.appendChild(li);
        });
      }
    }

    customElements.define('cart-discount-code-latori', CartDiscountCodeLatori);
  }
})();
